import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import ConfigPageLayout from '../components/ConfigPageLayout'

type AgentOption = {
  id: number
  name: string
  subtitle?: string | null
}

type GlobalConfigResponse = {
  public_agent_id?: number | null
  public_agent_pages?: string[]
  public_widget_enabled?: boolean
}

const PUBLIC_PAGE_OPTIONS: { value: string; label: string }[] = [
  { value: '*', label: 'Toutes les pages publiques (*)' },
  { value: '/', label: "Page d'accueil (/)" },
  { value: '/pricing', label: 'Tarifs (/pricing)' },
  { value: '/integrations', label: 'Intégrations (/integrations)' },
  { value: '/login', label: 'Connexion (/login)' },
  { value: '/agents', label: 'Agents (/agents)' },
  { value: '/subjects', label: 'Sujets (/subjects)' },
  { value: '/contents', label: 'Contenus (/contents)' },
]

const normalizePage = (value: string) => {
  if (!value) return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed === '*') return '*'
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return withSlash.replace(/\s+/g, '')
}

export default function PublicAgentConfigPage() {
  const [loading, setLoading] = useState(true)
  const [agentOptions, setAgentOptions] = useState<AgentOption[]>([])
  const [publicAgentId, setPublicAgentId] = useState<number | null>(null)
  const [publicPages, setPublicPages] = useState<string[]>([])
  const [publicWidgetEnabled, setPublicWidgetEnabled] = useState(true)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [customPageInput, setCustomPageInput] = useState('')

  const fetchConfig = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get<GlobalConfigResponse>('/global-config')
      setPublicAgentId(data.public_agent_id ?? null)
      setPublicPages(data.public_agent_pages || [])
      setPublicWidgetEnabled(data.public_widget_enabled ?? true)
    } catch (err: any) {
      setError(err?.message || 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchAgents = useCallback(async () => {
    try {
      const { data } = await api.get('/agents?limit=1000')
      const agents = Array.isArray(data) ? data : []
      setAgentOptions(
        agents.map((a: any) => ({
          id: a.id,
          name: a.name || 'Sans nom',
          subtitle: a.subtitle || null,
        }))
      )
    } catch (err) {
      console.error('Failed to fetch agents:', err)
    }
  }, [])

  useEffect(() => {
    fetchConfig()
    fetchAgents()
  }, [fetchConfig, fetchAgents])

  const handleAgentSelect = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value
    setPublicAgentId(value ? Number(value) : null)
    setStatus(null)
    setError(null)
  }, [])

  const togglePage = useCallback((page: string) => {
    setPublicPages((prev) => (prev.includes(page) ? prev.filter((p) => p !== page) : [...prev, page]))
    setStatus(null)
    setError(null)
  }, [])

  const handleAddCustomPage = useCallback(() => {
    const normalized = normalizePage(customPageInput)
    if (!normalized) return
    if (publicPages.includes(normalized)) {
      setError('Cette page est déjà dans la liste')
      return
    }
    setPublicPages((prev) => [...prev, normalized])
    setCustomPageInput('')
    setStatus(null)
    setError(null)
  }, [customPageInput, publicPages])

  const handleRemovePage = useCallback((page: string) => {
    setPublicPages((prev) => prev.filter((p) => p !== page))
    setStatus(null)
    setError(null)
  }, [])

  const handleSave = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      setSaving(true)
      setStatus(null)
      setError(null)

      try {
        await api.put('/global-config', {
          public_agent_id: publicAgentId,
          public_agent_pages: publicPages,
          public_widget_enabled: publicWidgetEnabled,
        })
        setStatus('Configuration mise à jour ✅')
      } catch (err: any) {
        const detail = err?.response?.data?.detail
        setError(typeof detail === 'string' ? detail : err?.message || 'Erreur lors de la sauvegarde')
      } finally {
        setSaving(false)
      }
    },
    [publicAgentId, publicPages, publicWidgetEnabled]
  )

  const customPages = useMemo(() => publicPages.filter((p) => !PUBLIC_PAGE_OPTIONS.some((opt) => opt.value === p)), [publicPages])

  if (loading) {
    return (
      <ConfigPageLayout title="AGENT PUBLIC" description="Configuration du widget public et de l'agent affiché">
        <div className="flex items-center justify-center py-12">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        </div>
      </ConfigPageLayout>
    )
  }

  return (
    <ConfigPageLayout title="AGENT PUBLIC" description="Configuration du widget public et de l'agent affiché">
      <form onSubmit={handleSave} className="space-y-6 rounded-3xl border border-emerald-100/80 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-zinc-900 dark:text-white">Activer le widget public</h3>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Désactivez temporairement le widget sur toutes les pages publiques
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setPublicWidgetEnabled(!publicWidgetEnabled)
                setStatus(null)
                setError(null)
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-900 ${
                publicWidgetEnabled ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-700'
              }`}
              disabled={saving}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                  publicWidgetEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-300" htmlFor="public-agent-select">
              Agent affiché publiquement
            </label>
            <select
              id="public-agent-select"
              value={publicAgentId ?? ''}
              onChange={handleAgentSelect}
              className="w-full rounded-xl border border-emerald-200/70 bg-white/80 px-4 py-2.5 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/50 dark:bg-zinc-950/60 dark:text-zinc-100 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
              disabled={saving}
            >
              <option value="">— Aucun agent public —</option>
              {agentOptions.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                  {agent.subtitle ? ` — ${agent.subtitle}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-zinc-600 dark:text-zinc-300">Pages publiques cibles</p>
            <div className="flex flex-wrap gap-2">
              {PUBLIC_PAGE_OPTIONS.map((option) => {
                const active = publicPages.includes(option.value)
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => togglePage(option.value)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                      active
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-500/70 dark:bg-emerald-900/40 dark:text-emerald-200'
                        : 'border-zinc-300 bg-white text-zinc-600 hover:border-emerald-200 hover:text-emerald-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
                    }`}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                value={customPageInput}
                onChange={(event) => setCustomPageInput(event.target.value)}
                placeholder="/mon-parcours ou *"
                className="w-full max-w-xs rounded-xl border border-zinc-200/70 bg-white/80 px-4 py-2 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-950/70 dark:text-zinc-100 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                disabled={saving}
              />
              <button
                type="button"
                onClick={handleAddCustomPage}
                className="rounded-full border border-emerald-200/70 bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/60"
                disabled={saving}
              >
                Ajouter une page
              </button>
            </div>

            {publicPages.length > 0 && (
              <ul className="flex flex-wrap gap-2">
                {publicPages.map((page) => (
                  <li key={page} className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-50 px-3 py-1 text-xs text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-900/40 dark:text-emerald-200">
                    <span>{page === '*' ? 'Toutes les pages (*)' : page}</span>
                    <button
                      type="button"
                      onClick={() => handleRemovePage(page)}
                      className="rounded-full border border-emerald-200/70 bg-white px-2 text-[10px] font-semibold text-emerald-600 transition hover:bg-emerald-100 dark:border-emerald-900/60 dark:bg-emerald-950/60 dark:text-emerald-200"
                    >
                      Retirer
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {status && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
              {status}
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
              {error}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              className="rounded-full border border-emerald-200/70 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/60"
              disabled={saving}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
            </button>
          </div>
        </form>
      </ConfigPageLayout>
  )
}
